/* ==========================================================================
   SkinTalk.mx - Cart Drawer JavaScript
   Custom element <cart-drawer>: open/close, section rendering API fetch,
   item quantity updates, item removal, upsell add, body scroll lock.
   ========================================================================== */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-cart-drawer-overlay]');
      this.panel = this.querySelector('#CartDrawerPanel');
      this.loadingEl = this.querySelector('[data-cart-loading]');
      this.sectionId = this.dataset.sectionId;
      this.triggerElement = null;
      this.isOpen = false;
    }

    connectedCallback() {
      this.setupTriggers();
      this.setupCloseHandlers();
      this.setupItemHandlers();
      this.setupUpsellHandlers();
      this.setupCartNoteHandler();

      // Listen for cart:updated to re-render
      SkinTalk.subscribe('cart:updated', () => {
        if (this.isOpen) {
          this.renderSection();
        }
      });

      // Listen for drawer:open events (guard against re-entry to prevent infinite loop)
      SkinTalk.subscribe('drawer:open', (data) => {
        if (data?.type === 'cart' && !this.isOpen) {
          this.open();
        }
      });
    }

    /* ------- Triggers ------- */
    setupTriggers() {
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.triggerElement = toggle;
          this.open();
        });
      });
    }

    /* ------- Close Handlers ------- */
    setupCloseHandlers() {
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      this.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    /* ------- Item Handlers ------- */
    setupItemHandlers() {
      this.addEventListener('click', (e) => {
        // Quantity minus
        const minusBtn = e.target.closest('[data-qty-minus]');
        if (minusBtn) {
          const line = parseInt(minusBtn.dataset.line);
          const valueEl = minusBtn.parentElement.querySelector('[data-qty-value]');
          const currentQty = parseInt(valueEl?.textContent) || 1;
          this.updateItemQuantity(line, Math.max(0, currentQty - 1));
          return;
        }

        // Quantity plus
        const plusBtn = e.target.closest('[data-qty-plus]');
        if (plusBtn) {
          const line = parseInt(plusBtn.dataset.line);
          const valueEl = plusBtn.parentElement.querySelector('[data-qty-value]');
          const currentQty = parseInt(valueEl?.textContent) || 1;
          this.updateItemQuantity(line, currentQty + 1);
          return;
        }

        // Remove item
        const removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          const line = parseInt(removeBtn.dataset.line);
          this.updateItemQuantity(line, 0);
          return;
        }
      });
    }

    /* ------- Upsell Handlers ------- */
    setupUpsellHandlers() {
      this.addEventListener('click', (e) => {
        const upsellBtn = e.target.closest('[data-upsell-add]');
        if (!upsellBtn) return;

        const variantId = upsellBtn.dataset.variantId;
        if (!variantId) return;

        this.addToCart(variantId, 1);
      });
    }

    /* ------- Cart Note Handler ------- */
    setupCartNoteHandler() {
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-cart-note]')) {
          this.updateCartNote(e.target.value);
        }
      });
    }

    /* ------- Open / Close ------- */
    open() {
      this.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      SkinTalk.lockBodyScroll();
      SkinTalk.publish('drawer:open', { type: 'cart' });

      if (this.panel) {
        setTimeout(() => {
          const closeBtn = this.querySelector('[data-cart-drawer-close]');
          SkinTalk.trapFocus(this.panel, closeBtn);
        }, 100);
      }

      // Refresh content when opening
      this.renderSection();
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      this.isOpen = false;
      SkinTalk.unlockBodyScroll();
      SkinTalk.removeTrapFocus();
      SkinTalk.publish('drawer:close', { type: 'cart' });

      if (this.triggerElement) {
        this.triggerElement.focus();
      }
    }

    /* ------- Loading State ------- */
    showLoading() {
      if (this.loadingEl) this.loadingEl.setAttribute('aria-hidden', 'false');
    }

    hideLoading() {
      if (this.loadingEl) this.loadingEl.setAttribute('aria-hidden', 'true');
    }

    /* ------- Section Rendering API ------- */
    async renderSection() {
      try {
        const response = await fetch(
          `${window.location.pathname}?section_id=${this.sectionId}`,
          { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
        );

        if (!response.ok) throw new Error('Section render failed');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('#CartDrawerPanel');

        if (newDrawer && this.panel) {
          // Replace panel content
          const header = newDrawer.querySelector('.cart-drawer__header');
          const body = newDrawer.querySelector('#CartDrawerBody');
          const footer = newDrawer.querySelector('[data-cart-footer]');
          const shippingBar = newDrawer.querySelector('[data-shipping-bar]');

          const currentHeader = this.panel.querySelector('.cart-drawer__header');
          const currentBody = this.panel.querySelector('#CartDrawerBody');
          const currentFooter = this.panel.querySelector('[data-cart-footer]');
          const currentShippingBar = this.panel.querySelector('[data-shipping-bar]');

          if (header && currentHeader) currentHeader.innerHTML = header.innerHTML;
          if (body && currentBody) currentBody.innerHTML = body.innerHTML;

          if (shippingBar && currentShippingBar) {
            currentShippingBar.innerHTML = shippingBar.innerHTML;
          } else if (shippingBar && !currentShippingBar) {
            currentHeader?.insertAdjacentElement('afterend', shippingBar);
          }

          if (footer && currentFooter) {
            currentFooter.innerHTML = footer.innerHTML;
          } else if (footer && !currentFooter) {
            this.panel.insertAdjacentHTML('beforeend', footer.outerHTML);
          } else if (!footer && currentFooter) {
            currentFooter.remove();
          }
        }
      } catch (error) {
        console.error('[SkinTalk] Cart drawer render error:', error);
      }
    }

    /* ------- Update Item Quantity ------- */
    async updateItemQuantity(line, quantity) {
      this.showLoading();

      try {
        const config = SkinTalk.fetchConfig();
        config.body = JSON.stringify({ line, quantity });

        const response = await fetch(SkinTalk.routes.cart_change_url + '.js', config);

        if (!response.ok) throw new Error('Cart update failed');

        const cart = await response.json();

        SkinTalk.publish('cart:updated', cart);

        await this.renderSection();
      } catch (error) {
        console.error('[SkinTalk] Error updating cart item:', error);
      } finally {
        this.hideLoading();
      }
    }

    /* ------- Add to Cart (Upsells) ------- */
    async addToCart(variantId, quantity = 1) {
      this.showLoading();

      try {
        const config = SkinTalk.fetchConfig();
        config.body = JSON.stringify({
          items: [{ id: parseInt(variantId), quantity }],
        });

        const response = await fetch(SkinTalk.routes.cart_add_url + '.js', config);

        if (!response.ok) throw new Error('Add to cart failed');

        // Fetch updated cart for event
        const cartResponse = await fetch(SkinTalk.routes.cart_js_url);
        const cart = await cartResponse.json();

        SkinTalk.publish('cart:updated', cart);

        await this.renderSection();
      } catch (error) {
        console.error('[SkinTalk] Error adding upsell to cart:', error);
      } finally {
        this.hideLoading();
      }
    }

    /* ------- Update Cart Note ------- */
    async updateCartNote(note) {
      try {
        const config = SkinTalk.fetchConfig();
        config.body = JSON.stringify({ note });

        await fetch(SkinTalk.routes.cart_update_url + '.js', config);
      } catch (error) {
        console.error('[SkinTalk] Error updating cart note:', error);
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
